export const nodesDocs = {
  "nodes-concepts": {
    title: "Conceitos e Uso de Nodes",
    description: "Guia fundamental para entender, configurar e conectar nodes dentro do ecossistema ASCFlow.",
    sections: [
      {
        title: "O que é um Node?",
        content: `
          <p>Um <strong>Node</strong> é a unidade básica de construção dentro do ASCFlow. Cada node representa uma ação específica (como enviar um email, fazer uma busca no banco de dados) ou uma lógica de controle (decidir qual caminho seguir, repetir uma ação).</p>
          <p class="mt-2">Ao conectar múltiplos nodes em sequência, você cria um <strong>Fluxo (Workflow)</strong> automatizado.</p>
        `
      },
      {
        title: "Estrutura e Funcionamento",
        content: `
          <div class="space-y-4">
            <div>
              <h3 class="font-bold text-lg">1. Entradas e Saídas (I/O)</h3>
              <p>Todo node possui pontos de conexão. A conexão **esquerda** recebe a execução vinda de um passo anterior. A conexão **direita** passa a execução para o próximo passo.</p>
            </div>
            <div>
              <h3 class="font-bold text-lg">2. Parâmetros</h3>
              <p>Ao clicar em um node, o painel de configuração se abre. Aqui você define os comportamentos estáticos (ex: URL de uma API) ou dinâmicos (ex: usar o email recebido no passo anterior).</p>
            </div>
            <div>
              <h3 class="font-bold text-lg">3. Resultado (JSON)</h3>
              <p>Após executar, todo node gera um resultado em formato JSON. Esse resultado fica salvo na memória do fluxo e pode ser usado por qualquer node subsequente através de Expressões.</p>
              <pre class="bg-muted p-2 rounded text-sm mt-1"><code>// Exemplo de retorno de um node
{
  "sucesso": true,
  "dados": {
    "id": 123,
    "nome": "Produto X"
  }
}</code></pre>
            </div>
          </div>
        `
      },
      {
        title: "Manipulando no Editor",
        content: `
           <ul class="list-disc pl-5 space-y-2">
            <li><strong>Adicionar:</strong> Arraste um node da barra lateral para a área de trabalho (canvas).</li>
            <li><strong>Conectar:</strong> Clique e arraste de um ponto de saída (bolinha direita) para um ponto de entrada (bolinha esquerda).</li>
            <li><strong>Testar (Execução Unitária):</strong> Você pode rodar um node individualmente clicando no ícone de "Play" no cabeçalho dele. Isso é essencial para validar configurações antes de rodar o fluxo todo.</li>
            <li><strong>Desativar:</strong> Nodes podem ser desativados temporariamente para não executarem durante o fluxo, mas mantendo a conexão passante.</li>
          </ul>
        `
      }
    ]
  },
  "node-schedule": {
    title: "Gatilho de Agendamento (Schedule)",
    description: "Inicia a execução do fluxo em intervalos de tempo pré-definidos ou horários específicos.",
    sections: [
      {
        title: "Visão Geral",
        content: `
          O node <strong>Schedule</strong> é um gatilho (Trigger) fundamental que permite automatizar a execução de fluxos baseados em tempo. 
          Ele é ideal para tarefas recorrentes como relatórios diários, limpezas de banco de dados semanais, ou verificações periódicas de APIs.
        `
      },
      {
        title: "Parâmetros de Configuração",
        content: `
          <div class="space-y-4">
            <div>
              <h3 class="font-bold text-lg">1. Recorrência (Recurrence)</h3>
              <p>Define a unidade de tempo para a repetição.</p>
              <ul class="list-disc pl-5 mt-2">
                <li><strong>Minutos/Horas:</strong> Para execuções frequentes.</li>
                <li><strong>Dias:</strong> Para execuções diárias (ex: Todo dia às 08:00).</li>
                <li><strong>Semanas:</strong> Para dias específicos da semana (ex: Toda segunda-feira).</li>
                <li><strong>Meses:</strong> Para dias específicos do mês (ex: Todo dia 05).</li>
                <li><strong>Cron Expression:</strong> Para configurações avançadas e personalizadas (ex: <code>0 8 * * 1-5</code>).</li>
              </ul>
            </div>

            <div>
              <h3 class="font-bold text-lg">2. A cada (Interval)</h3>
              <p>Define o intervalo da recorrência. Por exemplo, se a recorrência for "Dias" e o intervalo for "2", o fluxo rodará a cada 2 dias.</p>
            </div>

            <div>
              <h3 class="font-bold text-lg">3. Horários de Execução</h3>
              <p>Permite especificar horários exatos para a execução quando a recorrência é baseada em Dias ou Semanas. Você pode adicionar múltiplos horários.</p>
            </div>

            <div>
              <h3 class="font-bold text-lg">4. Data de Início e Fim</h3>
              <p>Define a janela temporal de validade da automação. O gatilho não disparará antes da Data de Início nem depois da Data de Fim.</p>
            </div>
          </div>
        `
      },
      {
        title: "Exemplo de Saída (JSON)",
        content: `
          Quando o gatilho é acionado, ele injeta no contexto informações sobre o momento da execução:
          <pre class="bg-muted p-4 rounded-md mt-2"><code>{
  "timestamp": 1704715200000,
  "readable": "2025-01-08T12:00:00.000Z",
  "cron_match": true
}</code></pre>
        `
      }
    ]
  },
  "node-webhook": {
    title: "Webhook Trigger",
    description: "Inicia a execução do fluxo ao receber uma requisição HTTP externa.",
    sections: [
      {
        title: "O que é um Webhook?",
        content: `
          <p>O <strong>Webhook</strong> é a porta de entrada para eventos em tempo real. Ele gera uma URL exclusiva que você cadastra em sistemas terceiros (como Typeform, Shopify, Trello, GitHub, Hotmart) para que o ASCFlow seja notificado sempre que algo importante acontecer.</p>
          <p class="mt-2">Ao contrário do node <em>Schedule</em> que "puxa" dados via agendamento, o Webhook "recebe" dados instantaneamente.</p>
        `
      },
      {
        title: "Configuração Passo-a-Passo",
        content: `
          <div class="space-y-6">
            <div>
              <h3 class="font-bold text-lg mb-2">1. Método HTTP</h3>
              <p>Define o verbo aceito pela rota. O mais comum é o <strong>POST</strong>, pois geralmente sistemas externos enviam dados no corpo (body) da requisição.</p>
              <ul class="list-disc pl-5 mt-1 text-sm text-muted-foreground">
                <li><code>POST</code>: Para receber dados (criação de pedidos, formulários).</li>
                <li><code>GET</code>: Para consultas simples ou validações.</li>
                <li><code>PUT/DELETE</code>: Para atualizações de estado ou remoções.</li>
              </ul>
            </div>

            <div>
              <h3 class="font-bold text-lg mb-2">2. Autenticação</h3>
              <p>Proteja sua rota para que apenas fontes confiáveis possam acioná-la.</p>
              <ul class="list-disc pl-5 mt-1">
                <li><strong>Public (Sem Auth):</strong> Qualquer pessoa com a URL pode acionar. Útil para testes ou formulários públicos.</li>
                <li><strong>Header Token:</strong> Exige um cabeçalho específico (ex: <code>x-api-key: 12345</code>).</li>
                <li><strong>Basic Auth:</strong> Exige usuário e senha codificados.</li>
              </ul>
            </div>

            <div>
              <h3 class="font-bold text-lg mb-2">3. Rota Personalizada</h3>
              <p>O sufixo da URL. Por exemplo, se definir <code>/novo-lead</code>, a URL final será algo como <code>https://api.ascflow.com/webhook/seu-id/novo-lead</code>.</p>
            </div>
          </div>
        `
      },
      {
        title: "Dados Recebidos (Output)",
        content: `
          <p>Quando o Webhook é acionado, ele disponibiliza automaticamente os seguintes dados para serem usados nas próximas etapas via Expressões:</p>
          <pre class="bg-muted p-3 rounded-md mt-2 text-sm overflow-x-auto"><code>// Exemplo de acesso via Expression
{{ json(get(trigger)).body.email }}      // Acessa o email enviado no JSON
{{ json(get(trigger)).headers.origin }} // Acessa um cabeçalho
{{ json(get(trigger)).query.id }}       // Acessa um parâmetro de URL (?id=123)</code></pre>
        `
      }
    ]
  },
  "node-webhook-response": {
    title: "Resposta Webhook",
    description: "Controle exatamente o que sua API retorna. Defina status, headers e o corpo da resposta para quem chamou seu fluxo.",
    sections: [
      {
        title: "1. Criando APIs Síncronas",
        content: `
          <div class="space-y-4">
            <p>O node <strong>Resposta Webhook</strong> transforma seu fluxo em uma API real. Ele encerra a conexão HTTP devolvendo os dados processados.</p>
            
            <div class="flex justify-center mb-6">
                <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                    <p class="text-sm m-0"><strong>Guia MDN:</strong> <a href="https://developer.mozilla.org/pt-BR/docs/Web/HTTP/Status" target="_blank" class="underline hover:text-primary">Códigos de Status HTTP</a></p>
                </div>
            </div>

            <ul class="list-disc pl-5 mt-2 space-y-2">
              <li><strong>Status Code:</strong> 200 (Sucesso), 201 (Criado), 400 (Erro), etc.</li>
              <li><strong>Corpo da Resposta:</strong> O JSON ou Texto que o solicitante irá receber.</li>
            </ul>
          </div>
        `
      }
    ]
  },

  "node-if": {
    title: "Condição (IF)",
    description: "Desvia o fluxo de execução para caminhos diferentes baseados em regras lógicas.",
    sections: [
      {
        title: "Entendendo o Fluxo Condicional",
        content: `
                            <p> O node <strong> IF </strong> é o 'cérebro' de decisão do seu workflow. Ele avalia uma regra e divide o caminho em dois:</p>
                          <ul class="list-disc pl-5 mt-2 mb-4">
                          <li><strong>Caminho TRUE(Verdadeiro): </strong> Executado se a condição for satisfeita. (Conexão superior direita)</li>
                          <li><strong>Caminho FALSE(Falso): </strong> Executado se a condição falhar. (Conexão inferior direita)</li>
                          </ul>
                            `
      },
      {
        title: "Modos de Configuração",
        content: `
                          <div class= "space-y-6">
                          <div>
                          <h3 class="font-bold text-lg mb-2"> 1. Visual Builder(Simples) </h3>
                          <p> Ideal para a maioria dos casos.Você monta a regra selecionando campos.</p>
                          <ul class= "list-disc pl-5 mt-2 bg-muted/50 p-2 rounded">
                          <li><strong>Campo: </strong> Selecione a variável (ex: <code>{{json(get(node-1)).status}}</code>).</li>
                            <li> <strong>Operador: </strong> Escolha a lógica (<code>Igual a</code>, <code>Contém </code>, <code>Maior que</code>, etc).</li>
                              <li> <strong>Valor: </strong> O dado de referência para comparação (ex: <code>aprovado</code>).</li>
                                </ul>
                                </div>

                                <div>
                                <h3 class="font-bold text-lg mb-2"> 2. Modo Expressão(Avançado) </h3>
                                  <p> Para lógicas complexas que o visual não cobre(ex: múltiplas condições E / OU, cálculos).Utilize JavaScript simples.</p>
                                    <pre class="bg-muted p-2 rounded text-sm mt-2"> <code>// Exemplo: Aprovar se for VIP E valor maior que 1000
                                      {{ json(get(node-1)).cliente_tipo }} == 'VIP' && {{ json(get(node-1)).valor }} > 1000 </code></pre>
                                        </div>
                                        </div>
                                          `
      }
    ]
  },
  "node-foreach": {
    title: "Repetição (ForEach)",
    description: "Executa um conjunto de nodes repetidamente para cada item de uma lista.",
    sections: [
      {
        title: "Como funciona o Loop",
        content: `
                                        <p> O <strong> ForEach </strong> pega uma lista (Array) e processa os nodes conectados dentro dele <strong>uma vez para cada item</strong> dessa lista.</p>
                                          <p class="mt-2"> Imagine que você buscou 50 clientes no banco de dados.O ForEach vai rodar 50 vezes, passando os dados do Cliente 1 na primeira vez, Cliente 2 na segunda, e assim por diante.</p>
                                            `
      },
      {
        title: "Configuração Detalhada",
        content: `
                                            <div class="space-y-4">
                                              <div>
                                              <h3 class="font-bold text-lg"> 1. Array para Iteração </h3>
                                                <p> O campo mais importante.Você deve apontar para onde está a lista de dados.Normalmente vem de um passo anterior.</p>
                                                  <p class="text-sm text-muted-foreground"> Exemplo: <code>{{ json(get(node-1)).rows }}</code></p>
                                                    </div>
                                                    </div>
                                                      `
      },
      {
        title: "Usando os Dados Dentro do Loop",
        content: `
                                                    <p> Dentro do loop, como saber qual item está sendo processado agora ? </p>
                                                      <p class="mt-2"> O ASCFlow disponibiliza um contexto especial para o item atual.Para acessar os dados do item da vez, use: </p>
                                                        <pre class="bg-muted p-2 rounded text-sm mt-2"> <code>{{ json(context).item }}        // O objeto completo do item atual
{{json(context).item.email}}  // Uma propriedade específica do item
{{json(context).index}}       // O número da volta (0, 1, 2...)</code></pre>
`
      }
    ]
  },
  "node-break": {
    title: "Interromper Loop (Break)",
    description: "Para imediatamente a execução de um loop ForEach.",
    sections: [
      {
        title: "Quando usar?",
        content: `
  <p> O node <strong> Break </strong> serve para otimizar processos. Imagine que você tem uma lista de 10.000 clientes e está procurando apenas um específico pelo CPF.</p>
    <p class="mt-2"> Sem o Break, o sistema continuaria rodando para os outros 9.999 clientes mesmo após encontrar quem você queria.Com o Break conectado após uma verificação IF(Encontrou ?), o loop para na hora.</p>
      `
      },
      {
        title: "Configuração",
        content: `
      <p> Este node não possui parâmetros complexos.Basta conectá - lo na saída de uma condição(no node IF) dentro de um loop.</p>
        <p class="mt-2 text-sm text-muted-foreground"> Nota: O Break só funciona se colocado dentro do escopo de um ForEach.Se usado fora, ele não terá efeito ou gerará erro.</p>
          `
      }
    ]
  },
  "node-sleep": {
    title: "Espera (Sleep)",
    description: "Pausa a execução do fluxo por um tempo determinado.",
    sections: [
      {
        title: "Para que serve?",
        content: `
          <p> O node <strong> Sleep </strong> força o robô a "tirar uma soneca". Isso é crucial em:</p>
            <ul class="list-disc pl-5 mt-2 space-y-1">
              <li><strong>Rate Limiting: </strong> Quando uma API aceita no máximo 10 requisições por segundo.</li>
                <li><strong>Sincronia: </strong> Aguardar um arquivo terminar de ser gerado por um sistema externo antes de tentar baixá-lo.</li>
                  <li><strong>Cadência: </strong> Simular comportamento humano em interações web.</li>
                    </ul>
                      `
      },
      {
        title: "Configuração",
        content: `
                    <ul class="list-disc pl-5 mt-2 space-y-2">
                      <li><strong>Tempo de Espera: </strong> O valor numérico da pausa (ex: 30, 5000).</li>
                        <li><strong>Unidade de Tempo: </strong>
                          <ul class="list-disc pl-5 mt-1 text-sm">
                            <li>Segundos(padrão) </li>
                            <li> Milissegundos(para ajustes finos) </li>
                            <li> Minutos </li>
                            </ul>
                            </li>
                            </ul>
                              `
      }
    ]
  },
  "node-execute-flow": {
    title: "Executar Fluxo",
    description: "Chama a execução de outro fluxo (Sub-flow), permitindo a modularização e reutilização de lógicas dentro do ASCFlow.",
    sections: [
      {
        title: "Visão Geral",
        content: `
          <p>O node <strong>Executar Fluxo</strong> permite que você invoque um segundo fluxo de automação de dentro do fluxo atual. Isso é ideal para:</p>
          <ul class="list-disc pl-5 mt-2 space-y-1">
            <li><strong>Modularização:</strong> Criar fluxos menores e focados em tarefas específicas.</li>
            <li><strong>Reutilização:</strong> Usar a mesma lógica (ex: envio de relatório complexo) em múltiplos fluxos diferentes.</li>
            <li><strong>Organização:</strong> Manter fluxos principais limpos e fáceis de ler.</li>
          </ul>
        `
      },
      {
        title: "Configuração dos Parâmetros",
        content: `
          <div class="space-y-4">
            <div>
              <h3 class="font-bold text-lg">1. Conexão</h3>
              <p>Selecione a credencial ou ambiente onde o fluxo destino está localizado.</p>
            </div>
            <div>
              <h3 class="font-bold text-lg">2. Usar ID Manual</h3>
              <p>Permite que você digite diretamente o ID do fluxo alvo. Caso desativado, você poderá selecionar o fluxo de uma lista suspensa.</p>
            </div>
            <div>
              <h3 class="font-bold text-lg">3. ID do Fluxo</h3>
              <p>O identificador único do fluxo que será executado. Você encontra esse ID na URL ou nas configurações do fluxo desejado.</p>
            </div>
            <div>
              <h3 class="font-bold text-lg">4. Parâmetros para o Fluxo Chamado</h3>
              <p>Aqui você define variáveis que serão enviadas para o fluxo filho (Sub-flow) que será executado. Use o botão <strong>+ Adicionar Parâmetro</strong> para criar pares de Nome e Valor.</p>
            </div>
          </div>
        `
      },
      {
        title: "Acessando os Dados no Fluxo Chamado",
        content: `
          <p>Dentro do fluxo que foi chamado (o fluxo filho), você pode acessar os valores passados pelo node pai utilizando a expressão <code>param()</code>:</p>
          <pre class="bg-muted p-2 rounded text-sm mt-2"><code>{{ param('nome_da_variavel') }}</code></pre>
          <p class="mt-2 text-sm text-muted-foreground">Exemplo: Se você enviou um parâmetro chamado <code>message</code> no node pai, lá no fluxo filho você usará <code>{{ param('message') }}</code> para recuperar o valor "Olá tudo bem?!" (conforme mostrado na imagem).</p>
        `
      }
    ]
  },
  "node-glpi-search": {
    title: "GLPI - Buscar Chamados",
    description: "Integração nativa para buscar tickets na plataforma GLPI.",
    sections: [
      {
        title: "1. Autenticação e Credenciais",
        content: `
                            <div class="space-y-4">
                              <p>Para buscar chamados no GLPI, você precisa habilitar a API REST nas configurações do seu servidor.</p>

                                <ul class="list-disc pl-5 mt-2 space-y-2">
                                  <li><strong>URL Base do GLPI: </strong> O link do seu portal GLPI terminando em <code>/apirest.php </code>.
                                    <br/> <span class= "text-xs text-muted-foreground"> Ex: <code>https://suaempresa.com/glpi/apirest.php</code></span>
</li>
  <li> <strong>App Token: </strong> Gerado em <em>Configurar> Geral> API</em>.Você deve registrar o ASCFlow como uma aplicação confiável.</li>
    <li> <strong>User Token: </strong> Encontrado no perfil do seu usuário, na aba <em>Chaves de acesso remoto</em>.</li>
      </ul>
      </div>
        `
      },
      {
        title: "2. Filtros de Busca",
        content: `
      <div class="space-y-4">
        <ul class="list-disc pl-5 space-y-2">
          <li><strong>Limite de Chamados: </strong> Define quantos resultados serão retornados por vez (ex: 50).</li>
            <li><strong>Filtros(JSON): </strong> Você pode usar a sintaxe nativa do GLPI para buscar por critérios específicos.
              <pre class="bg-muted p-2 rounded text-sm mt-1"> <code>{
                "status": "5",
                "users_id_recipient": "12"
              } </code></pre>
                </li>
                </ul>
                </div>
                  `
      }
    ]
  },
  "node-hubspot-contacts": {
    title: "HubSpot - Contatos",
    description: "Gerencia contatos no CRM HubSpot (Criar, Atualizar, Buscar).",
    sections: [
      {
        title: "1. Autenticação (Private App Token)",
        content: `
                <div class="space-y-4">
                  <p>A integração com o HubSpot utiliza <strong> Private Apps </strong> para garantir a segurança. Você precisará gerar um "Access Token" no seu portal HubSpot.</p>

                    <div class="flex justify-center mb-8">
                      <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                        <p class="text-sm m-0"> <strong>Documentação Oficial: </strong> <a href="https://developers.hubspot.com / docs / api / private - apps" target="_blank" class="underline hover:text-primary">Como criar um Private App no HubSpot</a></p>
                          </div>
                          </div>

                          <ul class="list-disc pl-5 mt-2 space-y-2">
                            <li><strong>Access Token: </strong> O token secreto gerado dentro do seu Private App. Ele começa com <code>pat-na1-...</code> ou semelhante.</li>
                              </ul>
                              </div>
                                `
      },
      {
        title: "2. Operação e Parâmetros",
        content: `
                              <div class="space-y-4">
                                <div>
                                <h3 class="font-bold text-lg"> Recurso: <code>contacts </code></h3>
                                  <p>Selecione a operação desejada: </p>
                                    <ul class="list-disc pl-5 mt-2 space-y-1">
                                      <li><strong>create: </strong> Cria um novo contato. Requer email.</li>
                                        <li><strong>update: </strong> Atualiza um contato existente (requer o ID do HubSpot).</li>
                                          <li><strong>search: </strong> Busca contatos usando filtros e propriedades.</li>
                                            </ul>
                                            </div>
                                            <div>
                                            <h3 class="font-bold text-lg"> Exemplo de Payload(JSON) </h3>
                                              <pre class="bg-muted p-2 rounded text-sm mt-1"> <code>{
                                                "properties": {
                                                  "email": "exemplo@hubspot.com",
                                                  "firstname": "João",
                                                  "lastname": "Silva",
                                                  "phone": "5511999999999"
                                                }
                                              } </code></pre>
                                                </div>
                                                </div>
                                                  `
      }
    ]
  },
  "node-hubspot-companies": {
    title: "HubSpot - Empresas",
    description: "Gerencia registros de empresas no HubSpot.",
    sections: [
      {
        title: "1. Autenticação (Access Token)",
        content: `
                                                <div class="space-y-4">
                                                  <p>Utilize o token do seu Private App HubSpot para autenticar as requisições de empresas.</p>
                                                    <div class="flex justify-center mb-6">
                                                      <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                                                        <p class="text-sm m-0"> <strong>Documentação Oficial: </strong> <a href="https://developers.hubspot.com / docs / api / private - apps" target="_blank" class="underline hover:text-primary">Como criar um Private App no HubSpot</a></p>
                                                          </div>
                                                          </div>
                                                          </div>
                                                            `
      },
      {
        title: "2. Operação",
        content: `
                                                          <ul class="list-disc pl-5 space-y-2">
                                                            <li><strong>Recurso: </strong> <code>companies</code> </li>
                                                              <li> <strong>Operação: </strong> Create, Update, Search.</li>
                                                                <li><strong>Parâmetros(JSON): </strong>
                                                                  <pre class="bg-muted p-2 rounded text-sm mt-1"> <code>{
                                                                    "properties": {
                                                                      "name": "Minha Empresa Ltda",
                                                                      "domain": "minhaempresa.com.br"
                                                                    }
                                                                  } </code></pre>
                                                                    </li>
                                                                    </ul>
                                                                      `
      }
    ]
  },
  "node-hubspot-deals": {
    title: "HubSpot - Negócios",
    description: "Cria e gerencia oportunidades de vendas (Deals) no pipeline do HubSpot.",
    sections: [
      {
        title: "1. Autenticação (Access Token)",
        content: `
                                                                    <div class="flex justify-center mb-6 mt-4">
                                                                      <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                                                                        <p class="text-sm m-0"> <strong>Documentação Oficial: </strong> <a href="https://developers.hubspot.com / docs / api / private - apps" target="_blank" class="underline hover:text-primary">Como criar um Private App no HubSpot</a></p>
                                                                          </div>
                                                                          </div>
                                                                            `
      },
      {
        title: "2. Operação e Pipelines",
        content: `
                                                                          <ul class="list-disc pl-5 space-y-2">
                                                                            <li><strong>Recurso: </strong> <code>deals</code> </li>
                                                                              <li> <strong>Operação: </strong> Create, Update.</li>
                                                                                <li><strong>Parâmetros(JSON): </strong>
                                                                                  <pre class="bg-muted p-2 rounded text-sm mt-1"> <code>{
                                                                                    "properties": {
                                                                                      "dealname": "Negócio Importante",
                                                                                      "amount": "1500.00",
                                                                                      "pipeline": "default",
                                                                                      "dealstage": "appointmentscheduled"
                                                                                    }
                                                                                  } </code></pre>
                                                                                    </li>
                                                                                    </ul>
                                                                                      `
      }
    ]
  },
  "node-hubspot-engagements": {
    title: "HubSpot - Compromissos",
    description: "Registra atividades como reuniões, chamadas ou tarefas associadas.",
    sections: [
      {
        title: "1. Autenticação (Access Token)",
        content: `
                                                                                    <div class="flex justify-center mb-6 mt-4">
                                                                                      <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                                                                                        <p class="text-sm m-0"> <strong>Documentação Oficial: </strong> <a href="https://developers.hubspot.com / docs / api / private - apps" target="_blank" class="underline hover:text-primary">Como criar um Private App no HubSpot</a></p>
                                                                                          </div>
                                                                                          </div>
                                                                                            `
      },
      {
        title: "2. Configuração",
        content: `
                                                                                          <ul class="list-disc pl-5 space-y-2">
                                                                                            <li><strong>Recurso: </strong> <code>engagements</code> (Reuniões, Chamadas, Tarefas).</li>
                                                                                              <li> <strong>Operação: </strong> Create.</li>
                                                                                                <li><strong>Parâmetros(JSON): </strong> Informe o tipo de engajamento e as propriedades de data e dono (owner).</li>
                                                                                                  </ul>
                                                                                                    `
      }
    ]
  },
  "node-hubspot-forms": {
    title: "HubSpot - Formulários",
    description: "Interage com a API de formulários para buscar campos e definições.",
    sections: [
      {
        title: "1. Autenticação (Access Token)",
        content: `
                                                                                                  <div class="flex justify-center mb-6 mt-4">
                                                                                                    <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                                                                                                      <p class="text-sm m-0"> <strong>Documentação Oficial: </strong> <a href="https://developers.hubspot.com / docs / api / private - apps" target="_blank" class="underline hover:text-primary">Como criar um Private App no HubSpot</a></p>
                                                                                                        </div>
                                                                                                        </div>
                                                                                                          `
      },
      {
        title: "2. Operação",
        content: `
                                                                                                        <ul class="list-disc pl-5 space-y-2">
                                                                                                          <li><strong>Recurso: </strong> <code>forms</code> </li>
                                                                                                            <li> <strong>Operação: </strong> <code>getFields</code> (Retorna os campos de um formulário especificamente).</li>
                                                                                                              <li> <strong>Parâmetros(JSON): </strong> Pode exigir o ID do formulário dependendo da operação.</li>
                                                                                                                </ul>
                                                                                                                  `
      }
    ]
  },
  "node-hubspot-tickets": {
    title: "HubSpot - Tickets",
    description: "Gerencia tickets de suporte no HubSpot Service Hub.",
    sections: [
      {
        title: "1. Autenticação (Access Token)",
        content: `
                                                                                                                <div class="flex justify-center mb-6 mt-4">
                                                                                                                  <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                                                                                                                    <p class="text-sm m-0"> <strong>Documentação Oficial: </strong> <a href="https://developers.hubspot.com / docs / api / private - apps" target="_blank" class="underline hover:text-primary">Como criar um Private App no HubSpot</a></p>
                                                                                                                      </div>
                                                                                                                      </div>
                                                                                                                        `
      },
      {
        title: "2. Operação",
        content: `
                                                                                                                      <ul class="list-disc pl-5 space-y-2">
                                                                                                                        <li><strong>Recurso: </strong> <code>tickets</code> </li>
                                                                                                                          <li> <strong>Operação: </strong> Create, Update.</li>
                                                                                                                            <li><strong>Parâmetros(JSON): </strong>
                                                                                                                              <pre class="bg-muted p-2 rounded text-sm mt-1"> <code>{
                                                                                                                                "properties": {
                                                                                                                                  "subject": "Erro no sistema",
                                                                                                                                  "hs_pipeline": "0",
                                                                                                                                  "hs_pipeline_stage": "1"
                                                                                                                                }
                                                                                                                              } </code></pre>
                                                                                                                                </li>
                                                                                                                                </ul>
                                                                                                                                  `
      }
    ]
  },
  "node-sharepoint": {
    title: "Microsoft SharePoint",
    description: "Realiza operações em arquivos e listas do Microsoft SharePoint.",
    sections: [
      {
        title: "1. Autenticação e Credenciais",
        content: `
                                                                                                                                <div class="space-y-4">
                                                                                                                                  <p>Para conectar ao SharePoint, você precisará registrar um aplicativo no <strong> Azure Active Directory(Entra ID) </strong> e coletar as seguintes informações:</p>

                                                                                                                                    <div class="flex justify-center mb-8">
                                                                                                                                      <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                                                                                                                                        <p class="text-sm m-0"> <strong>Documentação Oficial: </strong> <a href="https://learn.microsoft.com / pt - br / entra / identity - platform / quickstart - register - app" target="_blank" class="underline hover:text-primary">Como registrar um aplicativo no Microsoft Entra ID</a></p>
                                                                                                                                          </div>
                                                                                                                                          </div>

                                                                                                                                          <ul class="list-disc pl-5 mt-2 space-y-2">
                                                                                                                                            <li><strong>Nome Conexão: </strong> Um nome interno para identificar essa credencial (ex: "SharePoint Financeiro").</li>
                                                                                                                                              <li><strong>URL do Site SharePoint: </strong> O link base do seu site de equipe.
                                                                                                                                                <br/> <span class="text-xs text-muted-foreground"> Exemplo: <code>https://suaempresa.sharepoint.com/sites/nomedosite</code></span>
</li>
  <li> <strong>Client ID e Client Secret: </strong>
    <p class="text-sm mt-1"> Gerados ao registrar um App no Portal Azure(App Registrations).Permitem que o sistema acesse o SharePoint via API.</p>
      </li>
      <li> <strong>Tenant ID: </strong> O ID do diretório da sua organização no Azure.</li>
        <li><strong>Usuário e Senha: </strong>
          <p class="text-sm mt-1"> Credenciais de uma conta de serviço(Service Account) com permissão de acesso ao site especificado.Necessário para operações que exigem contexto de usuário delegada.</p>
            </li>
            </ul>
            </div>
              `
      },
      {
        title: "2. Configuração da Operação",
        content: `
            <ul class="list-disc pl-5 space-y-2">
              <li><strong>Nome da Lista / Biblioteca: </strong> O nome exato da lista ou pasta de documentos (ex: "Documentos Compartilhados", "Chamados").</li>
                <li><strong>Operação: </strong> Ação a ser executada no site:
                  <ul class="list-disc pl-5 mt-1 text-sm bg-muted p-2 rounded">
                    <li><code>Get Items </code>: Buscar metadados de itens ou arquivos.</li>
                      <li><code>Upload File </code>: Enviar um arquivo para uma biblioteca.</li>
                        <li><code>Create / Update Item </code>: Manipular registros em listas.</li>
                          </ul>
                          </li>
                          <li> <strong>Filtros(CAML / OData): </strong> Consultas para filtrar retornos (ex: <code>fields/Title eq 'Relatorio' </code>).</li>
                            </ul>
                              `
      }
    ]
  },
  "node-dashboard-generator": {
    title: "Dashboard Generator",
    description: "Gera relatórios visuais unificando dados de SQL, SharePoint e APIs.",
    sections: [
      {
        title: "1. Configuração Visual",
        content: `
                            <ul class="list-disc pl-5 space-y-2">
                              <li><strong>Template: </strong> Layout base do dashboard (ex: EXECUTIVE_SUMMARY).</li>
                                <li><strong>Título: </strong> Nome exibido no cabeçalho.</li>
                                  <li><strong>Tema Visual: </strong> Esquema de cores (ex: corporate, dark, light).</li>
                                    <li><strong>Estilos CSS: </strong> Personalização avançada da aparência.</li>
                                      </ul>
                                        `
      },
      {
        title: "2. Fontes de Dados",
        content: `
          O node permite conectar até 3 tipos de fontes simultaneamente:
<br/><br/>
  <strong>SQL Server: </strong>
    <ul class="list-disc pl-5 mb-2">
      <li>Query SQL direta(SELECT...).</li>
        <li> Nome da fonte para referência no template.</li>
          </ul>

          <strong> SharePoint: </strong>
            <ul class="list-disc pl-5 mb-2">
              <li>Nome da Lista e Campos específicos.</li>
                <li> Conexão dedicada.</li>
                  </ul>

                  <strong> Web API: </strong>
                    <ul class="list-disc pl-5 mb-2">
                      <li>Endpoint URL e Headers(Auth).</li>
                        <li> Ideal para buscar dados de sistemas terceiros.</li>
                          </ul>
                            `
      },
      {
        title: "3. Exportação e Performance",
        content: `
                          <ul class="list-disc pl-5 space-y-2">
                            <li><strong>Formatos: </strong> HTML (Interativo) ou PDF (Estático).</li>
                              <li><strong>Cache: </strong> Armazena resultados para carregamento rápido (True/False).</li>
                                <li> <strong>Intervalo de Atualização: </strong> Tempo em segundos para refresh dos dados.</li>
                                  </ul>
                                    `
      }
    ]
  },
  "node-azure-storage": {
    title: "Azure Storage",
    description: "Gerencia operações de armazenamento em nuvem no Azure (Blobs, Queues, etc).",
    sections: [
      {
        title: "1. Autenticação e Conexão",
        content: `
                                  <div class="space-y-4">
                                    <p>Para conectar ao Azure Storage, você precisará de uma <strong> Connection String </strong> válida ou do <strong>Account Name</strong> e <strong> Account Key </strong> da sua conta de armazenamento.</p>

                                      <div class="flex justify-center mb-8">
                                        <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                                          <p class="text-sm m-0"> <strong>Documentação Oficial: </strong> <a href="https://learn.microsoft.com / pt - br / azure / storage / common / storage - account - keys - manage" target="_blank" class="underline hover:text-primary">Gerenciar chaves de acesso da conta de armazenamento</a></p>
                                            </div>
                                            </div>

                                            <ul class="list-disc pl-5 mt-2 space-y-2">
                                              <li><strong>Connection String: </strong> A forma mais simples de conectar, contendo todos os parâmetros em uma única string alfanumérica.</li>
                                                <li><strong>Account Name: </strong> O nome exclusivo da sua conta de armazenamento no Azure.</li>
                                                  <li><strong>Access Key: </strong> Uma das duas chaves (key1 ou key2) geradas pelo Azure para autenticação.</li>
                                                    </ul>
                                                    </div>
                                                      `
      },
      {
        title: "2. Operações e Blobs",
        content: `
                                                    <div class="space-y-4">
                                                      <ul class="list-disc pl-5 space-y-2">
                                                        <li><strong>Recurso: </strong> Tipo de serviço (ex: <code>blob</code>).</li>
                                                          <li> <strong>Operação: </strong> Ação a executar (ex: <code>UPLOAD_BLOB</code>, <code>DOWNLOAD_BLOB </code>).</li>
                                                            <li><strong>Container: </strong> O balde (bucket) onde os arquivos residem. Deve estar previamente criado.</li>
                                                              <li><strong>Nome do Blob: </strong> Caminho e nome do arquivo dentro do container (ex: <code>documentos/nota_fiscal.pdf </code>).</li>
                                                                <li><strong>Conteúdo: </strong> O dado bruto do arquivo. Pode vir de um node anterior (ex: <code>{{ get(node-2).output }}</code>).</li>
                                                                  </ul>
                                                                  </div>
                                                                    `
      }
    ]
  },
  "node-postgresql": {
    title: "PostgreSQL",
    description: "Execute consultas SQL de alta performance diretamente em seu banco de dados PostgreSQL. Suporta operações de leitura e escrita com total flexibilidade.",
    sections: [
      {
        title: "1. Conexão e Segurança",
        content: `
                                                                  <div class="space-y-4">
                                                                    <p>Para conectar ao seu banco de dados PostgreSQL, utilize credenciais com as permissões exatas necessárias para o seu fluxo(Princípio do Menor Privilégio).</p>

                                                                      <div class="flex justify-center mb-6">
                                                                        <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                                                                          <p class="text-sm m-0"> <strong>Recurso Oficial: </strong> <a href="https://www.postgresql.org / docs / current / auth - methods.html" target="_blank" class="underline hover:text-primary">Métodos de Autenticação</a></p>
                                                                            </div>
                                                                            </div>

                                                                            <ul class="list-disc pl-5 mt-2 space-y-2">
                                                                              <li><strong>Host / IP: </strong> Endereço do servidor. Certifique-se de liberar o IP do Agente ASCFlow no seu firewall.</li>
                                                                                <li><strong>SSL Mode: </strong> Recomendamos o uso de conexões seguras (SSL/TLS) para ambientes de produção.</li>
                                                                                  <li> <strong>Porta Padrão: </strong> <code>5432</code>.</li>
                                                                                    </ul>
                                                                                    </div>
                                                                                      `
      },
      {
        title: "2. Execução de Queries Dinâmicas",
        content: `
                                                                                    <div class="space-y-4">
                                                                                      <p>Você pode utilizar o poder das <strong> Expressões AscFlow </strong> dentro das suas queries para criar consultas altamente dinâmicas.</p>

                                                                                        <div class="flex justify-center mb-6">
                                                                                          <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
                                                                                            <p class="text-sm m-0"> <strong>Guia de SQL: </strong> <a href="https://www.postgresql.org / docs / current / sql - commands.html" target="_blank" class="underline hover:text-primary">Referência de Comandos SQL</a></p>
                                                                                              </div>
                                                                                              </div>

                                                                                              <pre class="bg-muted p-2 rounded text-sm mt-1"> <code>--Exemplo de Select com Filtro Dinâmico
SELECT id, nome, email 
FROM usuarios 
WHERE id = {{ json(get(node-3)).body.user_id }}; </code></pre>

  <p class="text-sm text-muted-foreground mt-2"> Dica: Use <code> json() </code> para converter saídas de outros nodes em objetos acessíveis na sua query.</p>
    </div>
      `
      },
      {
        title: "3. Formato de Saída e Performance",
        content: `
    <div class="space-y-4">
      <p>Cada execução retorna um objeto JSON.Para consultas <code> SELECT </code>, o retorno será uma lista (array) de objetos representando as linhas.</p>

        <div class="flex justify-center mb-6">
          <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center">
            <p class="text-sm m-0"> <strong>Performance: </strong> <a href="https://www.postgresql.org / docs / current / performance - tips.html" target="_blank" class="underline hover:text-primary">Dicas de Performance</a></p>
              </div>
              </div>

              <p> <strong>Exemplo de Saída: </strong></p>
                <pre class="bg-muted p-3 rounded-md mt-2 text-sm overflow-x-auto"> <code>[
                  { "id": 1, "nome": "Usuário Teste", "email": "teste@exemplo.com" }
                ] </code></pre>

                  <p class="text-sm"> <strong>Timeout: </strong> Configure o tempo máximo para evitar travamentos em queries pesadas (Formato <code>HH:mm:ss</code>).</p>
                    </div>
                      `
      }
    ]
  },
  "node-openai-chat": {
    title: "OpenAI - Texto (Chat)",
    description: "Gera respostas de texto inteligentes e contextuais utilizando os modelos GPT da OpenAI.",
    sections: [
      {
        title: "1. Modelos e Capacidades",
        content: `
          <div class="space-y-4">
            <p>Escolha o modelo ideal para sua necessidade: <strong>GPT-4o</strong> para tarefas complexas ou <strong>GPT-3.5-Turbo</strong> para velocidade e custo-benefício.</p>

            <ul class="list-disc pl-5 mt-2 space-y-2">
              <li><strong>Mensagem do Sistema: </strong> Define a personalidade da IA (ex: "Você é um assistente financeiro").</li>
              <li><strong>Prompt do Usuário: </strong> A pergunta ou comando direto. Suporta variáveis do fluxo.</li>
              <li><strong>Max Tokens: </strong> Limite de comprimento da resposta.</li>
            </ul>
          </div>
        `
      },
      {
        title: "Exemplo Prático: Analista de Sentimento",
        content: `
          <div class="space-y-4">
            <p>Configure a mensagem do sistema para definir o comportamento e use dados do fluxo no prompt.</p>
            
            <div class="bg-card border rounded-lg overflow-hidden shrink-0">
              <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b">
                Configuração do Prompt
              </div>
              <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
                <span class="text-primary font-bold">[Sistema]:</span> Você é um analista de suporte. Responda se o cliente está satisfeito ou irritado.<br/>
                <span class="text-primary font-bold">[Usuário]:</span> Analise esta mensagem: "<span class="text-primary">{{ json(get(node-1)).mensagem_cliente }}</span>"
              </div>
            </div>
          </div>
        `
      }
    ]
  },
  "node-openai-image": {
    title: "OpenAI - Imagem",
    description: "Crie imagens originais e realistas a partir de descrições em texto usando DALL-E.",
    sections: [
      {
        title: "1. Criação com DALL-E",
        content: `
          <div class="space-y-4">
            <p>Transforme ideias em realidade visual. Utilize prompts detalhados para melhores resultados.</p>

            <ul class="list-disc pl-5 mt-2 space-y-2">
              <li><strong>Modelo: </strong> <code>dall-e-3</code> (Alta qualidade) ou <code>dall-e-2</code>.</li>
              <li><strong>Prompt: </strong> Descrição da imagem. Ex: "Um robô futurista pintando um quadro".</li>
              <li><strong>Tamanho: </strong> Resoluções como 1024x1024, 1024x1792, etc.</li>
            </ul>
          </div>
        `
      },
      {
        title: "Exemplo Prático: Banner de Produto",
        content: `
          <p class="text-sm mb-4">Crie banners personalizados automaticamente usando os dados do produto que vem de um banco de dados.</p>
          <div class="bg-card border rounded-lg overflow-hidden shrink-0">
            <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b">
              Prompt de Geração
            </div>
            <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
              Uma fotografia profissional de estilo publicitário para o produto <span class="text-primary">{{ json(get(node-3)).nome_produto }}</span>, fundo minimalista em cores <span class="text-primary">{{ json(get(node-3)).paleta_cores }}</span>, iluminação cinematográfica.
            </div>
          </div>
        `
      }
    ]
  },
  "node-openai-audio": {
    title: "OpenAI - Áudio",
    description: "Converta texto em fala de alta qualidade com vozes realistas (Text-to-Speech).",
    sections: [
      {
        title: "1. Síntese de Voz",
        content: `
          <div class="space-y-4">
            <p>Gere narrações para vídeos, assistentes de voz e muito mais com baixa latência.</p>

            <ul class="list-disc pl-5 mt-2 space-y-2">
              <li><strong>Vozes: </strong> <code>alloy</code>, <code>echo</code>, <code>fable</code>, <code>onyx</code>, <code>nova</code>, <code>shimmer</code>.</li>
              <li><strong>Formato: </strong> Retorna um stream de áudio (geralmente MP3).</li>
            </ul>
          </div>
        `
      },
      {
        title: "Exemplo Prático: Resposta por Voz",
        content: `
          <p class="text-sm mb-4">Crie uma experiência personalizada convertendo o texto de uma resposta em áudio para o cliente.</p>
          <div class="bg-card border rounded-lg overflow-hidden shrink-0">
            <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b">
              Texto para Conversão
            </div>
            <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
              "Olá <span class="text-primary">{{ json(get(node-1)).nome }}</span>, seu pedido já foi despachado!"
            </div>
          </div>
        `
      }
    ]
  },
  "node-openai-file": {
    title: "OpenAI - Arquivo",
    description: "Gerencie arquivos na infraestrutura da OpenAI para treinamento (Fine-tuning) ou uso com o Assistente.",
    sections: [
      {
        title: "1. Upload de Arquivos",
        content: `
          <div class="space-y-4">
            <p>Envie documentos, datasets ou arquivos de configuração para serem processados pela inteligência artificial.</p>

            <ul class="list-disc pl-5 mt-2 space-y-2">
              <li><strong>Propósito: </strong> Defina se o arquivo é para <code>assistants</code>, <code>fine-tune</code>, etc.</li>
              <li><strong>Formatos: </strong> Suporta PDF, JSONL, TXT e outros.</li>
            </ul>
          </div>
        `
      },
      {
        title: "Exemplo Prático: Base de Conhecimento",
        content: `
          <p class="text-sm mb-4">Envie o manual da sua empresa para que o assistente possa tirar dúvidas dos funcionários.</p>
          <div class="bg-card border rounded-lg overflow-hidden shrink-0">
            <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b">
              Arquivo para Upload
            </div>
            <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
              Caminho: <span class="text-primary">{{ get(node-2).caminho_manual_pdf }}</span>
            </div>
          </div>
        `
      }
    ]
  },
  "node-openai-function": {
    title: "OpenAI - Function Calling",
    description: "Dê 'superpoderes' aos seus modelos permitindo que eles decidam quando chamar ferramentas externas.",
    sections: [
      {
        title: "1. Definição de Ferramentas",
        content: `
          <div class="space-y-4">
            <p>Com Function Calling, o modelo não apenas responde texto, mas gera o JSON necessário para chamar uma função que você definiu.</p>

            <pre class="bg-muted p-2 rounded text-sm mt-1"> <code>// Exemplo de definição
{
  "name": "obter_estoque",
  "parameters": { "type": "object", "properties": { "id": { "type": "string"}} }
} </code></pre>
          </div>
        `
      },
      {
        title: "Exemplo Prático: Automação de E-commerce",
        content: `
          <p class="text-sm mb-4">O modelo reconhece que o usuário quer saber o estoque e gera a chamada da função automaticamente.</p>
          <div class="bg-card border rounded-lg overflow-hidden shrink-0">
            <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b">
              Pergunta do Usuário
            </div>
            <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
              "Tem disponibilidade do item <span class="text-primary">{{ get(node-1).id_produto }}</span> no depósito?"
            </div>
          </div>
        `
      }
    ]
  },
  "node-openai-embeddings": {
    title: "OpenAI - Embeddings",
    description: "Converta texto em vetores numéricos para buscas semânticas, recomendações e agrupamentos.",
    sections: [
      {
        title: "1. Vetorização de Dados",
        content: `
          <div class="space-y-4">
            <p>Embeddings são fundamentais para sistemas de RAG (Retrieval-Augmented Generation) e busca por similaridade.</p>

            <ul class="list-disc pl-5 mt-2 space-y-2">
              <li><strong>Modelos: </strong> <code>text-embedding-3-small</code> ou <code>large</code>.</li>
              <li><strong>Aplicações: </strong> Busca semântica, detecção de anomalias, classificação.</li>
            </ul>
          </div>
        `
      },
      {
        title: "Exemplo Prático: Busca Semântica",
        content: `
          <p class="text-sm mb-4">Transforme o conteúdo de um documento em números para encontrar informações relacionadas.</p>
          <div class="bg-card border rounded-lg overflow-hidden shrink-0">
            <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b">
              Input do Documento
            </div>
            <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
              Texto: "<span class="text-primary">{{ get(node-2).conteudo_pagina }}</span>"
            </div>
          </div>
        `
      }
    ]
  },
  "node-openai-structured-output": {
    title: "OpenAI - Saída Estruturada",
    description: "Garanta que a IA responda exatamente no formato que seu banco ou sistema espera, usando JSON Schema.",
    sections: [
      {
        title: "1. Precisão com JSON Schema",
        content: `
          <div class="space-y-4">
            <p>O Structured Output elimina a necessidade de tratar respostas de texto puro, garantindo 100% de conformidade com o schema definido.</p>

            <pre class="bg-muted p-2 rounded text-sm mt-1"> <code>// Exemplo de Schema
{
  "type": "object",
  "properties": { "valor": { "type": "number" }, "moeda": { "type": "string"}}
} </code></pre>
          </div>
        `
      },
      {
        title: "Exemplo Prático: Extração de Dados",
        content: `
          <p class="text-sm mb-4">Imagine receber um e-mail bagunçado e precisar extrair o valor de uma cobrança automaticamente.</p>
          <div class="bg-card border rounded-lg overflow-hidden shrink-0">
            <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b">
              Prompt de Extração
            </div>
            <div class="p-4 font-mono text-sm leading-relaxed text-foreground text-wrap">
              Extraia o valor total e a data de vencimento deste e-mail: "<span class="text-primary">{{ json(get(node-1)).corpo_email }}</span>". Responda estritamente no formato JSON definido.
            </div>
          </div>
        `
      }
    ]
  },
  "node-openai-web-search": {
    title: "OpenAI - Web Search",
    description: "Expanda o conhecimento da IA para além da data de corte, permitindo pesquisas em tempo real na internet.",
    sections: [
      {
        title: "1. Pesquisa em Tempo Real",
        content: `
          <div class="space-y-4">
            <p>Este node utiliza ferramentas de busca integradas para responder perguntas sobre eventos atuais ou dados que mudam constantemente.</p>
          </div>
        `
      },
      {
        title: "Exemplo Prático: Notícias Atuais",
        content: `
          <p class="text-sm mb-4">Mantenha seus usuários informados sobre o que está acontecendo agora.</p>
          <div class="bg-card border rounded-lg overflow-hidden shrink-0">
            <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b">
              Prompt de Busca
            </div>
            <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
              "Busque as últimas notícias sobre <span class="text-primary">{{ get(node-1).tema_interesse }}</span> e resuma o impacto para o mercado brasileiro."
            </div>
          </div>
        `
      }
    ]
  },
  "node-mssql": {
    title: "MS SQL Server",
    description: "Conecte e execute comandos Transact-SQL (T-SQL) no Microsoft SQL Server com total suporte a parâmetros dinâmicos.",
    sections: [
      {
        title: "1. Conexão e Autenticação",
        content: `
          <div class="space-y-4">
            <p>Para configurar a conexão, você precisará dos dados do servidor (Host), porta, banco de dados e credenciais de acesso.</p>

            <ul class="list-disc pl-5 mt-2 space-y-2">
              <li><strong>IP / Hostname: </strong> Endereço do servidor SQL Server.</li>
              <li><strong>Porta: </strong> A porta padrão geralmente é a <code>1433</code>.</li>
              <li> <strong>Instância: </strong> Caso utilize instâncias nomeadas (ex: <code>SQLEXPRESS</code>), certifique-se de configurar corretamente.</li>
              <li> <strong>Banco de Dados: </strong> Nome do banco (Initial Catalog).</li>
            </ul>
          </div>
        `
      },
      {
        title: "Exemplo Prático: Query Dinâmica",
        content: `
          <p class="text-sm mb-4">Execute comandos SQL usando informações capturadas em outros pontos do fluxo.</p>
          <div class="bg-card border rounded-lg overflow-hidden shrink-0">
            <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b">
              Query SQL
            </div>
            <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
              SELECT * FROM clientes WHERE email = '<span class="text-primary">{{ json(get(node-1)).email }}</span>'
            </div>
          </div>
        `
      },
      {
        title: "2. Queries Transact-SQL (T-SQL)",
        content: `
          <div class="space-y-4">
            <p>Escreva suas queries utilizando a sintaxe T-SQL. Você pode injetar variáveis do fluxo usando as chaves duplas.</p>

            <pre class="bg-muted p-2 rounded text-sm mt-1"> <code>-- Exemplo de Update com variável
UPDATE chamados 
SET status = 'Finalizado' 
WHERE id = <span class="text-primary">{{ json(get(node-1)).chamado_id }}</span></code> </pre>
          </div>
        `
      },
      {
        title: "3. Resultados e Performance",
        content: `
          <div class="space-y-4">
            <p>Os resultados são retornados em formato JSON estruturado, facilitando o uso em nodes de loop (ForEach) ou mapas.</p>
            <ul class="list-disc pl-5 space-y-2">
              <li><strong>Retornar Totais: </strong> Ative esta opção se precisar de metadados de contagem de linhas afetadas.</li>
              <li><strong>Formato de Saída: </strong> Sempre retorna um array de objetos para operações de leitura.</li>
            </ul>
          </div>
        `
      }
    ]
  },
  "node-mssql-job": {
    title: "MS SQL Server - Executar Job",
    description: "Acione a execução imediata de Jobs configurados no SQL Server Agent diretamente do seu fluxo.",
    sections: [
      {
        title: "1. Funcionamento dos Jobs",
        content: `
                    < div class="space-y-4" >
                      <p>Este node permite disparar processos agendados ou tarefas de manutenção que já foram criadas dentro do SQL Server Management Studio(SSMS).</p>

                        < div class="flex justify-center mb-6" >
                          <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                            <p class="text-sm m-0" > <strong>Recurso Oficial: </strong> <a href="https:/ / learn.microsoft.com / pt - br / sql / ssms / agent / sql - server - agent - jobs" target="_blank" class="underline hover: text - primary">Sobre SQL Server Agent Jobs</a></p>
                              </div>
                              </div>

                              < ul class="list-disc pl-5 mt-2 space-y-2" >
                                <li><strong>Nome do Job: </strong> Deve ser o nome exato cadastrado no servidor SQL.</li >
                                  <li><strong>Permissões: </strong> O usuário configurado na conexão deve ter permissão para iniciar jobs (geralmente membro da role <code>SQLAgentOperatorRole</code > no banco MSDB).</li>
                                    </ul>
                                    </div>
                                      `
      },
      {
        title: "2. Monitoramento de Execução",
        content: `
                                    < div class="space-y-4" >
                                      <p>Note que o node dispara o início do Job(Fire and Forget).Para monitorar o progresso, recomenda - se consultar as tabelas de histórico do MSDB.</p>

                                        < div class= "flex justify-center mb-6" >
                                        <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                          <p class="text-sm m-0" > <strong>Histórico de Jobs: </strong> <a href="https:/ / learn.microsoft.com / pt - br / sql / relational - databases / system - tables / dbo - sysjobhistory - transact - sql" target="_blank" class="underline hover: text - primary">Tabelas de Histórico (Sysjobhistory)</a></p>
                                            </div>
                                            </div>
                                            </div>
                                              `
      }
    ]
  },
  "node-web-data-extractor": {
    title: "Web Data Extractor",
    description: "Extraia informações estruturadas de qualquer site (Web Scraping) convertendo HTML em dados legíveis.",
    sections: [
      {
        title: "1. Scraping e Seletores",
        content: `
                                            < div class="space-y-4" >
                                              <p>Utilize seletores < strong > CSS < /strong> ou <strong>XPath</strong > para identificar quais partes do site você deseja capturar.</p>

                                                < div class="flex justify-center mb-6" >
                                                  <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                                    <p class="text-sm m-0" > <strong>Referência: </strong> <a href="https:/ / developer.mozilla.org / pt - BR / docs / Web / CSS / CSS_Selectors" target="_blank" class="underline hover: text - primary">Guia de Seletores CSS</a></p>
                                                      </div>
                                                      </div>

                                                      < ul class="list-disc pl-5 mt-2 space-y-2" >
                                                        <li><strong>Crawling: </strong> O agente visita a URL informada e navega pelos sublinks se configurado.</li >
                                                          <li><strong>JSON de Seletores: </strong> Mapeie campos como 'titulo', 'preco', 'descricao'.</li >
                                                            </ul>
                                                            </div>
                                                              `
      }
    ]
  },
  "node-email-send": {
    title: "Enviar Email",
    description: "Comunique-se com seus clientes enviando emails automáticos, alertas e relatórios.",
    sections: [
      {
        title: "1. SMTP e Provedores",
        content: `
                                                            < div class="space-y-4" >
                                                              <p>Configure seu servidor de saída(SMTP) ou utilize provedores integrados para garantir a entrega de suas mensagens.</p>

                                                                < div class="flex justify-center mb-6" >
                                                                  <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                                                    <p class="text-sm m-0" > <strong>Guia SMTP: </strong> <a href="https:/ / support.google.com / a / answer / 176600" target="_blank" class="underline hover: text - primary">Configurações SMTP Google</a></p>
                                                                      </div>
                                                                      </div>

                                                                      < ul class="list-disc pl-5 mt-2 space-y-2" >
                                                                        <li><strong>HTML Body: </strong> Crie emails ricos usando tags HTML.</li >
                                                                          <li><strong>Anexos: </strong> Envie arquivos gerados no fluxo diretamente por email.</li >
                                                                            </ul>
                                                                            </div>
                                                                              `
      }
    ]
  },
  "node-web-request": {
    title: "Web Request",
    description: "Realize chamadas HTTP (GET, POST, PUT, DELETE) para integrar com qualquer serviço web externo.",
    sections: [
      {
        title: "1. Integração com APIs",
        content: `
                                                                            < div class="space-y-4" >
                                                                              <p>O node < strong > Web Request < /strong> é o coração das integrações customizadas, permitindo que o ASCFlow converse com praticamente qualquer sistema na nuvem.</p >

                                                                                <div class="flex justify-center mb-6" >
                                                                                  <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                                                                    <p class="text-sm m-0" > <strong>Referência API: </strong> <a href="https:/ / developer.mozilla.org / pt - BR / docs / Web / HTTP / Methods" target="_blank" class="underline hover: text - primary">Métodos de Requisição HTTP</a></p>
                                                                                      </div>
                                                                                      </div>

                                                                                      < ul class="list-disc pl-5 mt-2 space-y-2" >
                                                                                        <li><strong>Método: </strong> Escolha entre GET, POST, etc.</li >
                                                                                          <li><strong>URL: </strong> O endpoint da API (suporta variáveis).</li >
                                                                                            <li><strong>Headers: </strong> Configure autenticação (Ex: <code>Authorization: Bearer ...</code >).</li>
                                                                                              </ul>
                                                                                              </div>
                                                                                                `
      }
    ]
  },
  "node-ado-users": {
    title: "Azure DevOps - Buscar Usuários",
    description: "Lista usuários de uma organização/projeto no Azure DevOps.",
    sections: [
      {
        title: "1. Autenticação e Credenciais",
        content: `
                                                                                              < div class="space-y-4" >
                                                                                                <p>Para conectar ao Azure DevOps, você precisará de um < strong > Personal Access Token(PAT) < /strong> com as permissões adequadas.</p >

                                                                                                  <div class="flex justify-center mb-8" >
                                                                                                    <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                                                                                      <p class="text-sm m-0" > <strong>Documentação Oficial: </strong> <a href="https:/ / learn.microsoft.com / pt - br / azure / devops / organizations / accounts / use - personal - access - tokens - to - authenticate" target="_blank" class="underline hover: text - primary">Como criar um PAT no Azure DevOps</a></p>
                                                                                                        </div>
                                                                                                        </div>

                                                                                                        < ul class="list-disc pl-5 mt-2 space-y-2" >
                                                                                                          <li><strong>Organização: </strong> O nome da sua organização que aparece na URL (ex: <code>dev.azure.com/NomeDaOrganizacao < /code>).</li >
                                                                                                            <li><strong>PAT Token: </strong> O token gerado no painel de segurança do Azure DevOps.</li >
                                                                                                              </ul>
                                                                                                              </div>
                                                                                                                `
      },
      {
        title: "2. Escopo da Busca",
        content: `
                                                                                                              < ul class="list-disc pl-5 space-y-2" >
                                                                                                                <li><strong>Projeto: </strong> Nome do projeto específico (opcional em algumas buscas globais).</li >
                                                                                                                  </ul>
                                                                                                                    `
      }
    ]
  },
  "node-ado-user": {
    title: "Azure DevOps - Buscar Usuário",
    description: "Busca detalhes de um usuário específico por ID ou Email.",
    sections: [
      {
        title: "1. Autenticação",
        content: `
                                                                                                                  < div class="flex justify-center mb-6 mt-4" >
                                                                                                                    <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                                                                                                      <p class="text-sm m-0" > <strong>Documentação Oficial: </strong> <a href="https:/ / learn.microsoft.com / pt - br / azure / devops / organizations / accounts / use - personal - access - tokens - to - authenticate" target="_blank" class="underline hover: text - primary">Como criar um PAT no Azure DevOps</a></p>
                                                                                                                        </div>
                                                                                                                        </div>
                                                                                                                          `
      },
      {
        title: "2. Filtros",
        content: `
                                                                                                                        < ul class="list-disc pl-5 space-y-2" >
                                                                                                                          <li><strong>Filtrar por: </strong> Escolha entre <code>ID</code > ou < code > Email < /code>.</li >
                                                                                                                            <li><strong>Identificador: </strong> O valor exato para a busca.</li >
                                                                                                                              <li><strong>PAT Token: </strong> Seu Personal Access Token.</li >
                                                                                                                                </ul>
                                                                                                                                  `
      }
    ]
  },
  "node-ado-workitems": {
    title: "Azure DevOps - Buscar Workitems (WIQL)",
    description: "Executa uma consulta WIQL para buscar itens de trabalho.",
    sections: [
      {
        title: "1. Autenticação",
        content: `
                                                                                                                                < div class="flex justify-center mb-6 mt-4" >
                                                                                                                                  <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                                                                                                                    <p class="text-sm m-0" > <strong>Documentação Oficial: </strong> <a href="https:/ / learn.microsoft.com / pt - br / azure / devops / organizations / accounts / use - personal - access - tokens - to - authenticate" target="_blank" class="underline hover: text - primary">Como criar um PAT no Azure DevOps</a></p>
                                                                                                                                      </div>
                                                                                                                                      </div>
                                                                                                                                        `
      },
      {
        title: "2. Consulta WIQL",
        content: `
                                                                                                                                      < ul class="list-disc pl-5 space-y-2" >
                                                                                                                                        <li><strong>Consulta WIQL: </strong> Query semelhante ao SQL para filtrar itens.
                                                                                                                                          < pre class="bg-muted p-2 rounded text-sm mt-1" > <code>SELECT[System.Id], [System.Title] 
FROM WorkItems
WHERE[System.State] = 'Active' AND[System.TeamProject] = 'MeuProjeto' < /code></pre >
  </li>
  < li > <strong>PAT Token: </strong> Token de acesso com permissões de WorkItems.</li >
    </ul>
      `
      }
    ]
  },
  "node-ado-workitem": {
    title: "Azure DevOps - Buscar Workitem (ID)",
    description: "Retorna detalhes completos de um único Workitem.",
    sections: [
      {
        title: "1. Autenticação",
        content: `
    < div class="flex justify-center mb-6 mt-4" >
      <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
        <p class="text-sm m-0" > <strong>Documentação Oficial: </strong> <a href="https:/ / learn.microsoft.com / pt - br / azure / devops / organizations / accounts / use - personal - access - tokens - to - authenticate" target="_blank" class="underline hover: text - primary">Como criar um PAT no Azure DevOps</a></p>
          </div>
          </div>
            `
      },
      {
        title: "2. Identificação",
        content: `
          < ul class="list-disc pl-5 space-y-2" >
            <li><strong>Workitem ID: </strong> O número sequencial do item no Azure DevOps.</li >
              <li><strong>PAT Token: </strong> Seu token de acesso.</li >
                </ul>
                  `
      }
    ]
  },
  "node-ado-workitems-recursive": {
    title: "Azure DevOps - Workitems Recorrentes",
    description: "Busca workitems alterados a partir de uma data específica.",
    sections: [
      {
        title: "1. Autenticação",
        content: `
                < div class="flex justify-center mb-6 mt-4" >
                  <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                    <p class="text-sm m-0" > <strong>Documentação Oficial: </strong> <a href="https:/ / learn.microsoft.com / pt - br / azure / devops / organizations / accounts / use - personal - access - tokens - to - authenticate" target="_blank" class="underline hover: text - primary">Como criar um PAT no Azure DevOps</a></p>
                      </div>
                      </div>
                        `
      },
      {
        title: "2. Filtro Temporal",
        content: `
                      < ul class="list-disc pl-5 space-y-2" >
                        <li><strong>Alterados desde: </strong> Define o ponto de partida para a busca de atualizações.</li >
                          <li><strong>Projeto: </strong> Nome do projeto no Azure DevOps.</li >
                            </ul>
                              `
      }
    ]
  },
  "node-ado-workitems-history": {
    title: "Azure DevOps - Histórico de WorkItems",
    description: "Recupera o histórico de revisões e alterações de workitems específicos.",
    sections: [
      {
        title: "1. Autenticação",
        content: `
                            < div class="flex justify-center mb-6 mt-4" >
                              <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                <p class="text-sm m-0" > <strong>Documentação Oficial: </strong> <a href="https:/ / learn.microsoft.com / pt - br / azure / devops / organizations / accounts / use - personal - access - tokens - to - authenticate" target="_blank" class="underline hover: text - primary">Como criar um PAT no Azure DevOps</a></p>
                                  </div>
                                  </div>
                                    `
      },
      {
        title: "2. Seleção de Itens",
        content: `
                                  < ul class="list-disc pl-5 space-y-2" >
                                    <li><strong>Organização: </strong> Nome da conta ADO.</li >
                                      <li><strong>WorkItem IDs: </strong> Lista de IDs separados por vírgula (ex: <code>101, 102, 105</code >).</li>
                                        </ul>
                                          `
      }
    ]
  },
  "node-html-writer": {
    title: "HTML Writer",
    description: "Crie layouts HTML dinâmicos para relatórios, dashboards ou corpos de email personalizados.",
    sections: [
      {
        title: "1. Composição de Documentos",
        content: `
                                        < div class="space-y-4" >
                                          <p>Utilize o editor para compor o HTML.Você pode injetar dados do fluxo no meio das tags.</p>

                                            < div class="flex justify-center mb-6" >
                                              <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                                <p class="text-sm m-0" > <strong>Aprenda HTML: </strong> <a href="https:/ / www.w3schools.com / html / " target="_blank" class="underline hover: text - primary">Tutorial de HTML</a></p>
                                                  </div>
                                                  </div>

                                                  < pre class="bg-muted p-2 rounded text-sm mt-1" > <code>& lt; div & gt;
  & lt; h1 & gt; Olá, {{ json(trigger).name }}& lt;/h1&gt;
  & lt; p & gt;Seu saldo é R$ { { get('saldo') } }& lt;/p&gt;
& lt; /div&gt;</code > </pre>
  </div>
    `
      }
    ]
  },
  "node-linkedin-users": {
    title: "LinkedIn - Users Associados",
    description: "Busca perfis de usuários associados a uma página de empresa no LinkedIn.",
    sections: [
      {
        title: "Visão Geral",
        content: `
  < p > Este node permite extrair listas de funcionários públicos de uma Company Page no LinkedIn.É útil para enriquecimento de leads e mapeamento de personas.</p>
    `
      },
      {
        title: "Configuração",
        content: `
    < ul class="list-disc pl-5 space-y-2" >
      <li><strong>Empresa(URN / ID): </strong> O identificador numérico da empresa ou o "vanity name" da URL (ex: <code>microsoft</code > em linkedin.com / company / microsoft).</li>
        < li > <strong>Cookie de Sessão(li_at): </strong> Necessário para autenticação. O node simula uma navegação logada.</li >
          <li><strong>Filtros(Opcional): </strong> Palavras-chave de cargo ou localização.</li >
            </ul>
              `
      }
    ]
  },
  "node-html-to-pdf": {
    title: "HTML -> PDF",
    description: "Transforme seus documentos HTML em arquivos PDF profissionais com alta fidelidade visual.",
    sections: [
      {
        title: "1. Geração de PDF",
        content: `
            < div class="space-y-4" >
              <p>Converta faturas, recibos e relatórios HTML em arquivos PDF prontos para arquivamento ou envio direto ao cliente.</p>

                < div class="flex justify-center mb-6" >
                  <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                    <p class="text-sm m-0" > <strong>Padrão de Impressão: </strong> <a href="https:/ / www.iso.org / standard / 63534.html" target="_blank" class="underline hover: text - primary">Sobre o formato PDF</a></p>
                      </div>
                      </div>

                      < ul class="list-disc pl-5 mt-2 space-y-2" >
                        <li><strong>Formato: </strong> A4, Letter, etc.</li >
                          <li><strong>Qualidade: </strong> Gera arquivos otimizados para web ou impressão.</li >
                            </ul>
                            </div>
                              `
      }
    ]
  },
  "node-converter": {
    title: "Converter (Multiformato)",
    description: "A ferramenta física para transformação de formatos. Converta dados e documentos entre diversos padrões de mercado.",
    sections: [
      {
        title: "1. Transformações Suportadas",
        content: `
                            < div class="space-y-4" >
                              <p>O < strong > Converter < /strong> resolve o problema de compatibilidade entre sistemas, permitindo que você manipule codificações e extraia texto de documentos sem uma linha de código.</p >

                              <div class="flex justify-center mb-6" >
                                <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                  <p class="text-sm m-0" > <strong>Referência de Codificação: </strong> <a href="https:/ / developer.mozilla.org / pt - BR / docs / Glossary / Base64" target="_blank" class="underline hover: text - primary">O que é Base64?</a></p>
                                    </div>
                                    </div>

                                    < div class="grid grid-cols-1 md:grid-cols-2 gap-4" >
                                      <div class="bg-muted/50 p-3 rounded-lg border" >
                                        <h4 class="font-bold text-primary mb-2 flex items-center gap-2" >
                                          <span>🔤</span> Texto e Base64
                                            </h4>
                                            < ul class="text-xs space-y-1 text-muted-foreground" >
                                              <li>• Texto para Base64 </li>
                                                <li>• Base64 para Texto </li>
                                                  <li>• JSON para Base64 CSV </li>
                                                    <li>• Buffer para Base64 </li>
                                                      <li>• Base64 para Buffer </li>
                                                        </ul>
                                                        </div>
                                                        < div class="bg-muted/50 p-3 rounded-lg border" >
                                                          <h4 class="font-bold text-primary mb-2 flex items-center gap-2" >
                                                            <span>🖼️</span> Imagens e Mídia
                                                              </h4>
                                                              < ul class="text-xs space-y-1 text-muted-foreground" >
                                                                <li>• Imagem para Base64 </li>
                                                                  <li>• Base64 para Imagem </li>
                                                                    <li>• Áudio(mp3 / wav) para Base64 </li>
                                                                      <li>• HTML para Imagem </li>
                                                                        <li>• PNG para Base64(UTF - 8) </li>
                                                                          </ul>
                                                                          </div>
                                                                          < div class="bg-muted/50 p-3 rounded-lg border" >
                                                                            <h4 class="font-bold text-primary mb-2 flex items-center gap-2" >
                                                                              <span>📄</span> Extração de Documentos
                                                                                </h4>
                                                                                < ul class="text-xs space-y-1 text-muted-foreground" >
                                                                                  <li>• Base64 PDF para Base64 TXT </li>
                                                                                    <li>• Base64 DOC para Base64 TXT </li>
                                                                                      <li>• Base64 XLSX para Base64 CSV </li>
                                                                                        <li>• Base64 MSG para Base64 TXT </li>
                                                                                          </ul>
                                                                                          </div>
                                                                                          < div class="bg-muted/50 p-3 rounded-lg border" >
                                                                                            <h4 class="font-bold text-primary mb-2 flex items-center gap-2" >
                                                                                              <span>🔄</span> Web e Diversos
                                                                                                </h4>
                                                                                                < ul class="text-xs space-y-1 text-muted-foreground" >
                                                                                                  <li>• Imagem para Blob / Blob para Base64 </li>
                                                                                                    <li>• octet - stream para Base64 </li>
                                                                                                      <li>• URL Decode / Encode </li>
                                                                                                        <li>• XML para JSON </li>
                                                                                                          </ul>
                                                                                                          </div>
                                                                                                          </div>
                                                                                                          </div>
                                                                                                            `
      },
      {
        title: "2. Configuração Geral",
        content: `
                                                                                                          < ul class="list-disc pl-5 space-y-3" >
                                                                                                            <li><strong>Tipo de Conversão: </strong> Selecione a regra de transformação desejada.</li >
                                                                                                              <li><strong>Entrada(Input): </strong> O dado bruto que será transformado (variável ou expressão).</li >
                                                                                                                </ul>
                                                                                                                  `
      }
    ]
  },
  "node-add-variable": {
    title: "Add Variável",
    description: "Gerencia variáveis de contexto, permitindo criar, definir valores ou incrementar listas.",
    sections: [
      {
        title: "O Poder do Contexto",
        content: `
                                                                                                                < p > Durante a execução de um fluxo, às vezes você precisa salvar uma informação temporária para usar lá na frente(ex: um contador, uma flag de erro, ou acumular itens em uma nova lista).</p>
                                                                                                                  < p class="mt-2" > O node < strong > Add Variável < /strong> cria ou atualiza esses dados na memória global do fluxo.</p >
                                                                                                                    `
      },
      {
        title: "Configuração Detalhada",
        content: `
                                                                                                                    < div class="space-y-4" >
                                                                                                                      <div>
                                                                                                                      <h3 class="font-bold text-lg" > 1. Nome da Variável </h3>
                                                                                                                        < p > O identificador único.Se a variável já existir, ela será atualizada.Se não, será criada.Dê nomes claros(ex: <code>total_vendas < /code>, <code>lista_emails</code >).</p>
                                                                                                                          </div>
                                                                                                                          < div >
                                                                                                                          <h3 class="font-bold text-lg" > 2. Tipo de Dado </h3>
                                                                                                                            < ul class="list-disc pl-5 mt-1" >
                                                                                                                              <li><strong>String(Texto): </strong> Para textos simples.</li >
                                                                                                                                <li><strong>Number(Número): </strong> Para cálculos matemáticos.</li >
                                                                                                                                  <li><strong>Array(Lista): </strong> Uma coleção de itens.</li >
                                                                                                                                    <li><strong>Boolean: </strong> Verdadeiro ou Falso.</li >
                                                                                                                                      <li><strong>JSON: </strong> Um objeto complexo.</li >
                                                                                                                                        </ul>
                                                                                                                                        </div>
                                                                                                                                        < div >
                                                                                                                                        <h3 class="font-bold text-lg" > 3. Incremento em Listas(Array Push) </h3>
                                                                                                                                          < p > Se você selecionar o tipo < strong > Array < /strong>, surge o campo especial <strong>"Incrementar"</strong >.Isso é muito poderoso dentro de loops: </p>
                                                                                                                                            < p class="text-sm mt-1 bg-muted p-2 rounded" > Ao invés de substituir a lista toda, ele < em > adiciona < /em> o novo item ao final da lista existente. Útil para construir relatórios linha a linha.</p >
                                                                                                                                              </div>
                                                                                                                                              < div >
                                                                                                                                              <h3 class="font-bold text-lg" > 4. Valor(Value) </h3>
                                                                                                                                                < p > O conteúdo que você quer salvar.Pode ser um valor fixo ou vir de outro node(ex: <code>{{ json(get(trigger)).body.id}}</code>).</p >
                                                                                                                                                  </div>
                                                                                                                                                  </div>
                                                                                                                                                    `
      }
    ]
  },
  "node-execute-process": {
    title: "Exec. Processo (Shell)",
    description: "Executa programas ou comandos do sistema operacional (Shell/CMD) onde o agente está rodando.",
    sections: [
      {
        title: "Execução Local",
        content: `
                                                                                                                                                  < p > Este node permite que o ASCFlow interaja diretamente com o servidor onde o agente está instalado.É como abrir o terminal(CMD ou Bash) e rodar um comando.</p>
                                                                                                                                                    < p class="mt-2 font-semibold text-red-500" >⚠ Atenção para questões de segurança ao rodar comandos arbitrários.</p>
                                                                                                                                                      `
      },
      {
        title: "Parâmetros e Dicas",
        content: `
                                                                                                                                                      < ul class="list-disc pl-5 space-y-4" >
                                                                                                                                                        <li><strong>Executável: </strong>
                                                                                                                                                          < p > O comando principal.Pode ser um binário do sistema(<code>python < /code>, <code>node</code >, <code>curl < /code>) ou um caminho absoluto para um script (<code>C:\\Scripts\\meu_robo.bat</code >).</p>
                                                                                                                                                            </li>
                                                                                                                                                            < li > <strong>Argumentos: </strong>
                                                                                                                                                              < p > Parâmetros passados para o comando, separados por espaço.Se tiver espaços no valor, use aspas.</p>
                                                                                                                                                                < p class="text-sm text-muted-foreground" > Ex: <code>script.py--data "2023-01-01" --verbose < /code></p >
                                                                                                                                                                  </li>
                                                                                                                                                                  < li > <strong>Diretório de Trabalho(CWD): </strong>
                                                                                                                                                                    < p > Em qual pasta o comando deve ser executado ? Importante se seu script faz referências a arquivos relativos(ex: <code>./ config.json < /code>).</p >
                                                                                                                                                                      </li>
                                                                                                                                                                      </ul>
                                                                                                                                                                        `
      }
    ]
  },
  "node-object-transform": {
    title: "Transformação de Objeto (Mapper)",
    description: "Mapeia e transforma uma lista de objetos (JSON) para um novo formato.",
    sections: [
      {
        title: "Padronização de Dados (De/Para)",
        content: `
                                                                                                                                                                      < p > Muitas vezes, a saída de um sistema(A) não é compatível com a entrada de outro sistema(B).O sistema A envia < code > { "nm_usuario": "João" } < /code> mas o B espera <code>{ "full_name": "João" }</code >.</p>
                                                                                                                                                                    < p class= "mt-2" > O node < strong > Transformação de Objeto < /strong> resolve isso, reescrevendo a estrutura dos dados em massa.</p >
                                                                                                                                                                    `
      },
      {
        title: "Configuração",
        content: `
                                                                                                                                                                    < div class= "space-y-4" >
                                                                                                                                                                    <div>
                                                                                                                                                                    <h3 class="font-bold text-lg" > 1. Array de Entrada </h3>
                                                                                                                                                                    < p > A lista original que será processada.Ex: <code>{{ json(get(node - 1)).rows }}</code>.</p >
                                                                                                                                                                      </div>
                                                                                                                                                                      < div >
                                                                                                                                                                      <h3 class="font-bold text-lg" > 2. Mapeamento Visual </h3>
                                                                                                                                                                        < p > Você define o "schema" de saída.Crie os campos novos e arraste ou digite de onde vem o valor.</p>
                                                                                                                                                                          < ul class="list-disc pl-5 mt-1 text-sm bg-muted p-2 rounded" >
                                                                                                                                                                            <li><strong>Campo Destino: </strong> O nome da chave no novo JSON (ex: <code>full_name</code >).</li>
                                                                                                                                                                              < li > <strong>Valor Origem: </strong> O caminho no JSON antigo (ex: <code>nm_usuario</code >).</li>
                                                                                                                                                                                </ul>
                                                                                                                                                                                </div>
                                                                                                                                                                                < div >
                                                                                                                                                                                <h3 class="font-bold text-lg" > 3. Transformação Avançada(Manual) </h3>
                                                                                                                                                                                  < p > Se precisar usar fórmulas complexas na transformação, você pode escrever o JSON de mapeamento manualmente.</p>
                                                                                                                                                                                    </div>
                                                                                                                                                                                    </div>
                                                                                                                                                                                      `
      }
    ]
  },
  "node-powerbi-refresh": {
    title: "Power BI - Atualizar Conjunto",
    description: "Dispara a atualização de um conjunto de dados (Dataset) no Power BI.",
    sections: [
      {
        title: "1. Autenticação e IDs",
        content: `
                                                                                                                                                                                    < div class="space-y-4" >
                                                                                                                                                                                      <p>Para atualizar um conjunto de dados, você precisará dos identificadores(IDs) do Workspace e do Dataset, além de um token de acesso válido.</p>

                                                                                                                                                                                        < div class="flex justify-center mb-8" >
                                                                                                                                                                                          <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                                                                                                                                                                            <p class="text-sm m-0" > <strong>Documentação Oficial: </strong> <a href="https:/ / learn.microsoft.com / en - us / rest / api / power - bi / datasets / refresh - dataset" target="_blank" class="underline hover: text - primary">Power BI REST API - Refresh Dataset</a></p>
                                                                                                                                                                                              </div>
                                                                                                                                                                                              </div>

                                                                                                                                                                                              < ul class="list-disc pl-5 mt-2 space-y-2" >
                                                                                                                                                                                                <li><strong>Conjunto(Dataset ID): </strong> Encontrado na URL do Power BI ao visualizar o conjunto.
                                                                                                                                                                                                  < br /> <span class="text-xs text-muted-foreground" > Ex: <code>.../datasets/{ ID_AQUI } /details</code > </span>
                                                                                                                                                                                                    </li>
                                                                                                                                                                                                    < li > <strong>Token: </strong> Bearer Token ou Chave de Acesso para autorizar a requisição.</li >
                                                                                                                                                                                                      <li><strong>Url Token: </strong> Endpoint de autenticação caso utilize renovação automática.</li >
                                                                                                                                                                                                        </ul>
                                                                                                                                                                                                        </div>
                                                                                                                                                                                                          `
      }
    ]
  },
  "node-powerbi-print": {
    title: "Power BI - Print",
    description: "Gera um print (imagem) de um relatório do Power BI via engine de browser.",
    sections: [
      {
        title: "1. Configuração do Relatório",
        content: `
                                                                                                                                                                                                        < div class="space-y-4" >
                                                                                                                                                                                                          <p>Este node acessa o relatório via navegador para capturar a imagem.Certifique - se de que o relatório esteja acessível pelas credenciais informadas.</p>

                                                                                                                                                                                                            < div class="flex justify-center mb-6 mt-4" >
                                                                                                                                                                                                              <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                                                                                                                                                                                                <p class="text-sm m-0" > <strong>Documentação Oficial: </strong> <a href="https:/ / learn.microsoft.com / pt - br / rest / api / power - bi / reports /export -to - file" target="_blank" class="underline hover: text - primary">Power BI REST API - Export to File</a></p>
                                                                                                                                                                                                                  </div>
                                                                                                                                                                                                                  </div>

                                                                                                                                                                                                                  < ul class="list-disc pl-5 space-y-2" >
                                                                                                                                                                                                                    <li><strong>URL do Relatório: </strong> O link direto do Power BI Service.
                                                                                                                                                                                                                      < br /> <span class= "text-xs text-muted-foreground" > Formato: <code>https://app.powerbi.com/groups/{workspace_id}/reports/{report_id}/...</code></span>
</li>
  < li > <strong>Usuário / Senha: </strong> Credenciais de serviço com permissão de visualização.</li >
    <li><strong>Seletor CSS do Filtro: </strong> Caso precise clicar em algum filtro específico antes da captura.</li >
      <li><strong>Timeout(segundos): </strong> Tempo para aguardar o carregamento dos visuais antes do "print".</li >
        </ul>
        </div>
          `
      }
    ]
  },
  "node-google-drive": {
    title: "Google Drive - Operações",
    description: "Gerencia arquivos no Google Drive (Copiar, Mover, Renomear, etc).",
    sections: [
      {
        title: "1. Autenticação e Credenciais",
        content: `
        < div class="space-y-4" >
          <p>Para interagir com o Google Drive, você precisará de uma credencial OAuth válida configurada no Google Cloud Console.</p>

            < div class="flex justify-center mb-8" >
              <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                <p class="text-sm m-0" > <strong>Documentação Oficial: </strong> <a href="https:/ / developers.google.com / drive / api / guides / enable - drive - api" target="_blank" class="underline hover: text - primary">Como habilitar a API e obter credenciais do Google Drive</a></p>
                  </div>
                  </div>

                  < ul class="list-disc pl-5 mt-2 space-y-2" >
                    <li><strong>OAuth Token: </strong> O token de acesso que permite ao ASCFlow ler ou escrever no seu Drive.</li >
                      </ul>
                      </div>
                        `
      },
      {
        title: "2. Configuração da Operação",
        content: `
                      < div class="space-y-4" >
                        <ul class="list-disc pl-5 space-y-2" >
                          <li><strong>Nome do Fluxo(opcional): </strong> Identificador para organização interna do log de execução.</li >
                            <li><strong>Operação: </strong> Ação a ser realizada. Exemplos: <code>copyFile</code > (Copiar), <code>moveFile < /code> (Mover), <code>deleteFile</code > (Apagar), <code>createFolder < /code> (Criar Pasta).</li >
                              <li><strong>ID do Arquivo: </strong> O identificador único do arquivo que você quer manipular.
                                < br /> <span class= "text-xs text-muted-foreground" > Dica: Você encontra o ID na URL do arquivo no navegador(após o<code> / d / </code>).</span >
                                  </li>
                                  < li > <strong>Nome do Arquivo: </strong> Nome que o arquivo terá após a operação (ex: para Renomear ou Copiar).</li >
                                    <li><strong>ID da Pasta Destino: </strong> Se estiver movendo ou copiando, informe o ID da pasta para onde o arquivo deve ir.</li >
                                      </ul>
                                      </div>
                                        `
      }
    ]
  },
  "node-odbc-query": {
    title: "Exec. Query SQL (ODBC)",
    description: "Execute comandos SQL em praticamente qualquer banco de dados (Legacy ou Moderno) utilizando a interface padrão ODBC.",
    sections: [
      {
        title: "1. Drivers e DSN",
        content: `
                                      < div class="space-y-4" >
                                        <p>O ODBC(Open Database Connectivity) depende de drivers instalados no servidor do Agente.Você pode utilizar um < strong > DSN(Data Source Name) < /strong> configurado no SO ou uma <strong>Connection String</strong > direta.</p>

                                          < div class="flex justify-center mb-6" >
                                            <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                              <p class="text-sm m-0" > <strong>Recurso Oficial: </strong> <a href="https:/ / learn.microsoft.com / pt - br / sql / odbc / microsoft - open - database - connectivity - odbc" target="_blank" class="underline hover: text - primary">O que é ODBC? (Microsoft)</a></p>
                                                </div>
                                                </div>

                                                < ul class="list-disc pl-5 mt-2 space-y-2" >
                                                  <li><strong>DSN: </strong> Nome da fonte de dados configurada no 'Administrador de Dados ODBC' do Windows ou <code>odbc.ini</code > no Linux.</li>
                                                    < li > <strong>Drivers Comuns: </strong> SQL Server, Oracle, MySQL, IBM DB2, Sybase, entre outros.</li >
                                                      </ul>
                                                      </div>
                                                        `
      },
      {
        title: "2. Execução de Comandos",
        content: `
                                                      < div class="space-y-4" >
                                                        <p>Este node é universal.Aceita qualquer comando SQL válido suportado pelo seu driver específico.</p>

                                                          < div class="flex justify-center mb-6" >
                                                            <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
                                                              <p class="text-sm m-0" > <strong>Referência Técnica: </strong> <a href="https:/ / github.com / markitondemand / node - odbc" target="_blank" class="underline hover: text - primary">Documentação do Driver Node-ODBC</a></p>
                                                                </div>
                                                                </div>

                                                                < pre class="bg-muted p-2 rounded text-sm mt-1" > <code>--Exemplo genérico
SELECT * FROM tabela_legada 
WHERE cod_ref = '{{ get(node-1) }}'; </code></pre >

  <p class="text-sm text-muted-foreground" > O ASCFlow gerencia a abertura e fechamento da conexão automaticamente a cada execução.</p>
    </div>
      `
      },
      {
        title: "3. Formato de Resposta",
        content: `
    < p > O retorno segue o padrão JSON do ASCFlow, convertendo automaticamente os tipos de dados do driver para tipos nativos do JavaScript(onde possível).</p>
      < pre class= "bg-muted p-3 rounded-md mt-2 text-sm overflow-x-auto" > <code>[
        { "COLUNA_A": "valor", "COLUNA_B": 12345 }
      ] < /code></pre >
      `
      }
    ]
  },
  "node-whatsapp-api": {
    title: "Whatsapp API",
    description: "Envia mensagens ou requisições para API do Whatsapp Business.",
    sections: [
      {
        title: "1. Conexão e Autenticação",
        content: `
      < div class="space-y-4" >
        <p>Para enviar mensagens via API oficial, você deve utilizar o < strong > Facebook Developer Portal < /strong> e configurar o Cloud API.</p >

          <div class="flex justify-center mb-8" >
            <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
              <p class="text-sm m-0" > <strong>Documentação Oficial: </strong> <a href="https:/ / developers.facebook.com / docs / whatsapp / cloud - api / get - started" target="_blank" class="underline hover: text - primary">Introdução à Cloud API do WhatsApp</a></p>
                </div>
                </div>

                < ul class="list-disc pl-5 mt-2 space-y-2" >
                  <li><strong>URL da API: </strong> O endpoint da Graph API do Facebook.
                    < br /> <span class="text-xs text-muted-foreground" > Ex: <code>https://graph.facebook.com/v19.0/{PHONE_NUMBER_ID}/messages</code></span>
</li>
  < li > <strong>Bearer Token: </strong> O token de acesso gerado no Gerenciador de Negócios da Meta.</li >
    </ul>
    </div>
      `
      },
      {
        title: "2. Configuração do Envio (Payload)",
        content: `
    < div class="space-y-4" >
      <p>O < strong > Payload < /strong> é o corpo da mensagem em formato JSON. Ele define quem recebe e o conteúdo (texto, imagem ou template).</p >

      <div>
      <h3 class="font-bold text-lg" > Exemplo: Envio de Template </h3>
        < pre class="bg-muted p-2 rounded text-sm mt-1" > <code>{
          "messaging_product": "whatsapp",
          "to": "5511999999999",
          "type": "template",
          "template": {
            "name": "hello_world",
            "language": { "code": "en_US" }
          }
        } < /code></pre >
          </div>

          < div class="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-md border border-blue-100 dark:border-blue-900" >
            <p class="text-xs text-blue-800 dark:text-blue-300" >
                    💡 <strong>Dica: </strong> Certifique-se de que o número de destino esteja no formato internacional (DDI + DDD + Número).
  </p>
  </div>
  </div>
    `
      }
    ]
  },
  "node-google-search": {
    title: "WebSearch Google",
    description: "Utilize o poder de busca do Google diretamente dentro do seu fluxo para encontrar informações atualizadas de qualquer site.",
    sections: [
      {
        title: "1. Google Custom Search",
        content: `
  < div class="space-y-4" >
    <p>Configure sua própria engine de busca personalizada e receba resultados em formato JSON estruturado.</p>

      < div class="flex justify-center mb-6" >
        <div class="bg-card p-3 px-10 rounded-md border shadow-sm w-fit text-center" >
          <p class="text-sm m-0" > <strong>Configuração: </strong> <a href="https:/ / developers.google.com / custom - search / v1 / overview" target="_blank" class="underline hover: text - primary">Google Programmable Search</a></p>
            </div>
            </div>

            < ul class="list-disc pl-5 mt-2 space-y-2" >
              <li><strong>Query: </strong> A pergunta ou termo de busca.</li >
                <li><strong>Resultados: </strong> Retorna títulos, links e snippets (resumos) de cada página encontrada.</li >
                  </ul>
                  </div>
                    `
      }
    ]
  },
  "node-gemini-chat": {
    title: "Google Gemini",
    description: "Utilize os modelos de IA mais avançados do Google para processamento de texto e lógica.",
    sections: [
      {
        title: "1. Configuração Gemini",
        content: `
                  < div class="space-y-4" >
                    <p>Integre com o ecossistema Google AI Studio para acessar modelos como o < strong > Gemini 1.5 Pro < /strong> ou <strong>Flash</strong >.</p>

                      < ul class="list-disc pl-5 mt-2 space-y-2" >
                        <li><strong>API Key: </strong> Chave obtida no <a href="https:/ / aistudio.google.com / " target="_blank" class="underline">Google AI Studio</a>.</li>
                          < li > <strong>Prompt: </strong> Instruções baseadas em texto ou multimodais.</li >
                            </ul>
                            </div>
                              `
      },
      {
        title: "Exemplo Prático: Análise de Imagem/Documento",
        content: `
                            < p class="text-sm mb-4" > O Gemini é excelente para entender contextos complexos e arquivos variados.</p>
                              < div class="bg-card border rounded-lg overflow-hidden shrink-0" >
                                <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b" >
                                  Prompt de Análise
                                    </div>
                                    < div class="p-4 font-mono text-sm leading-relaxed text-foreground" >
                                      Descreva os pontos principais deste PDF: "<span class="text - primary">{{ get(node-2).url_documento }}</span>" e resuma em 3 bullets.
            </div>
                                        </div>
                                          `
      }
    ]
  },
  "node-smart-agent-call": {
    title: "Smart Agent Hub - Call",
    description: "Invoque habilidades específicas e ferramentas de seus Agentes Autônomos configurados no Hub.",
    sections: [
      {
        title: "1. Funções do Agente",
        content: `
                                        < div class="space-y-4" >
                                          <p>Utilize as "Calls" para que seu fluxo execute ações complexas que foram delegadas a um agente inteligente, como análises profundas ou chamadas a sistemas externos.</p>

                                            < ul class="list-disc pl-5 mt-2 space-y-2" >
                                              <li><strong>Calls Disponíveis: </strong> Dependem do agente selecionado no Hub.</li >
                                                <li><strong>Parâmetros: </strong> Envie as instruções necessárias para a execução da tarefa.</li >
                                                  </ul>
                                                  </div>
                                                    `
      }
    ]
  },
  "node-smart-agent-chat": {
    title: "Smart Agent Hub - Agente",
    description: "Estabeleça um diálogo com um Agente Autônomo para resolver problemas complexos via conversação.",
    sections: [
      {
        title: "1. Interação com Agente",
        content: `
                                                  < div class="space-y-4" >
                                                    <p>Conecte - se aos agentes do seu Hub para processar demandas de forma natural.Ideal para triagens, suporte e orquestração de tarefas.</p>
                                                      </div>
                                                        `
      },
      {
        title: "Exemplo Prático: Agente de Suporte Avançado",
        content: `
                                                      < p class="text-sm mb-4" > O agente pode decidir se deve enviar um orçamento ou agendar uma reunião baseando - se no contexto.</p>
                                                        < div class="bg-card border rounded-lg overflow-hidden shrink-0" >
                                                          <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b" >
                                                            Instrução para o Agente
                                                              </div>
                                                              < div class="p-4 font-mono text-sm leading-relaxed text-foreground" >
                                                                Analise os interesses do cliente < span class= "text-primary" > {{ json(get(node - 1)).nome }}</span>: "<span class="text-primary">{{ json(get(node-1)).interesses }}</span > " e sugira o plano mais adequado.
                                                                  </div>
                                                                  </div>
                                                                    `
      }
    ]
  },

};
